// Header پوسته (IA §2.1، §5): دکمه جستجوی مراجعه‌کننده (Ctrl+K — SCR-STAFF-100) و منوی «+ سریع» با «مراجعه‌کننده جدید» (Alt+N —
// SCR-STAFF-101)، «نوبت جدید» (Alt+B) و «مراجعه بدون نوبت» (Alt+W — AP-SCH-01). فقط با Permission مربوط نمایش داده و فعال
// می‌شوند. «ثبت و ادامه به نوبت» دیالوگ رزرو را با مراجعه‌کننده پیش‌انتخاب باز می‌کند (FLOW-01 ← FLOW-02؛ بدون Permission رزرو
// پرونده — C-AGENT-313)؛ «فقط ثبت» با Snackbar و لینک پرونده.
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { PatientView } from '@phoenix/api-client';
import { useAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits } from '@phoenix/ui';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useSchedulingActions } from '../scheduling/SchedulingActions';
import { patientPath } from './common';
import { TemporaryRegisterDialog } from './IncompletePatientsPage';
import { PatientRegisterDialog, type RegisterCompletion } from './PatientRegisterDialog';
import { PatientSearchPalette } from './PatientSearchPalette';

interface PatientActions {
  openSearch: () => void;
  openRegister: (prefill?: string) => void;
  /** ثبت موقت (US-PAT-007 — AP-PAT-02). */
  openTemporary: () => void;
}

const PatientActionsContext = createContext<PatientActions | null>(null);

/** اقدام‌های سراسری مراجعه‌کننده (برای صفحات دیگر، مثل دکمه «مراجعه‌کننده جدید» صفحه فهرست). */
export function usePatientActions(): PatientActions | null {
  return useContext(PatientActionsContext);
}

/** ارائه‌دهنده اقدام‌ها و میانبرهای سراسری Ctrl+K و Alt+N؛ دیالوگ‌ها یک بار در پوسته. */
export function PatientActionsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('pat');
  const access = useAccess();
  const navigate = useNavigate();
  const scheduling = useSchedulingActions();
  const canView = access.can('pat.patient.view');
  const canCreate = access.can('pat.patient.create') && access.accessMode === 'Full';
  const [searchOpen, setSearchOpen] = useState(false);
  const [register, setRegister] = useState<{ open: boolean; prefill?: string }>({ open: false });
  const [registered, setRegistered] = useState<PatientView | null>(null);
  const [temporaryOpen, setTemporaryOpen] = useState(false);

  const openSearch = useCallback(() => {
    if (canView) {
      setSearchOpen(true);
    }
  }, [canView]);
  const openRegister = useCallback(
    (prefill?: string) => {
      if (canCreate) {
        setSearchOpen(false);
        setRegister({ open: true, prefill });
      }
    },
    [canCreate],
  );

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openSearch();
      } else if (
        event.altKey &&
        !event.ctrlKey &&
        (event.code === 'KeyN' || event.key.toLowerCase() === 'n')
      ) {
        event.preventDefault();
        openRegister();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [openSearch, openRegister]);

  const openPatient = useCallback(
    (id: string) => {
      setSearchOpen(false);
      setRegister({ open: false });
      void navigate(patientPath(id));
    },
    [navigate],
  );

  const onRegistered = (patient: PatientView, completion: RegisterCompletion) => {
    setRegister({ open: false });
    if (completion === 'continue' && scheduling?.canBook) {
      scheduling.openBooking({
        patient: { id: patient.id, displayName: patient.displayName, fileNumber: patient.fileNumber },
      });
    } else if (completion === 'continue') {
      void navigate(patientPath(patient.id));
    } else {
      setRegistered(patient);
    }
  };

  const openTemporary = useCallback(() => {
    if (canCreate) {
      setTemporaryOpen(true);
    }
  }, [canCreate]);

  const value = useMemo(
    () => ({ openSearch, openRegister, openTemporary }),
    [openSearch, openRegister, openTemporary],
  );

  return (
    <PatientActionsContext.Provider value={value}>
      {children}
      {/* فقط هنگام باز بودن Mount می‌شود: بستن هم‌زمان با ناوبری گاهی انیمیشن خروج MUI را تمام نمی‌کرد و Modal نامرئی بقیه صفحه را
          از درخت دسترس‌پذیری پنهان نگه می‌داشت (C-AGENT-1368). */}
      {canView && searchOpen && (
        <PatientSearchPalette
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onOpenPatient={openPatient}
          onRegister={canCreate ? openRegister : undefined}
        />
      )}
      {canCreate && (
        <PatientRegisterDialog
          open={register.open}
          prefill={register.prefill}
          onClose={() => setRegister({ open: false })}
          onRegistered={onRegistered}
          onOpenPatient={openPatient}
        />
      )}
      {canCreate && (
        <TemporaryRegisterDialog
          open={temporaryOpen}
          onClose={() => setTemporaryOpen(false)}
          onCreated={(patient) => {
            setTemporaryOpen(false);
            openPatient(patient.id);
          }}
        />
      )}
      <Snackbar open={registered !== null} autoHideDuration={6000} onClose={() => setRegistered(null)}>
        <Alert
          severity="success"
          onClose={() => setRegistered(null)}
          data-testid="register-success"
          action={
            registered ? (
              <Button color="inherit" size="small" onClick={() => openPatient(registered.id)}>
                {t('pat:register.open_profile')}
              </Button>
            ) : undefined
          }
        >
          {registered
            ? t('pat:register.success', {
                fileNumber: faDigits(registered.fileNumber),
                name: registered.displayName,
              })
            : null}
        </Alert>
      </Snackbar>
    </PatientActionsContext.Provider>
  );
}

/** دکمه‌های Header: جستجو (Ctrl+K) و منوی «+ سریع». */
export function PatientHeaderActions() {
  const { t } = useTranslation('pat');
  const access = useAccess();
  const actions = usePatientActions();
  const scheduling = useSchedulingActions();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!actions || !access.can('pat.patient.view')) {
    return null;
  }
  const canCreate = access.can('pat.patient.create') && access.accessMode === 'Full';
  const canBook = scheduling?.canBook ?? false;
  const canWalkIn = scheduling?.canWalkIn ?? false;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<SearchIcon />}
        onClick={actions.openSearch}
        data-testid="header-patient-search"
        aria-keyshortcuts="Control+K"
        sx={{ minWidth: 220, justifyContent: 'flex-start' }}
      >
        <Typography variant="body2" component="span" sx={{ flexGrow: 1, textAlign: 'start' }}>
          {t('pat:quick.search')}
        </Typography>
        <Typography variant="caption" component="kbd" dir="ltr" color="text.secondary">
          {t('pat:quick.search_shortcut')}
        </Typography>
      </Button>
      {(canCreate || canBook) && (
        <>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(event) => setAnchor(event.currentTarget)}
            data-testid="header-quick-menu"
            aria-haspopup="menu"
          >
            {t('pat:quick.menu')}
          </Button>
          <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
            {canCreate && (
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  actions.openRegister();
                }}
                data-testid="quick-new-patient"
              >
                <Typography sx={{ flexGrow: 1 }}>{t('pat:quick.new_patient')}</Typography>
                <Typography variant="caption" component="kbd" dir="ltr" color="text.secondary" sx={{ ml: 2 }}>
                  {t('pat:quick.new_patient_shortcut')}
                </Typography>
              </MenuItem>
            )}
            {canCreate && (
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  actions.openTemporary();
                }}
                data-testid="quick-temporary-patient"
              >
                <Typography sx={{ flexGrow: 1 }}>{t('pat:temporary.title')}</Typography>
              </MenuItem>
            )}
            {canBook && (
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  scheduling?.openBooking();
                }}
                data-testid="quick-new-appointment"
              >
                <Typography sx={{ flexGrow: 1 }}>{t('sch:quick.new_appointment')}</Typography>
                <Typography variant="caption" component="kbd" dir="ltr" color="text.secondary" sx={{ ml: 2 }}>
                  {t('sch:quick.new_appointment_shortcut')}
                </Typography>
              </MenuItem>
            )}
            {canWalkIn && (
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  scheduling?.openWalkIn();
                }}
                data-testid="quick-walk-in"
              >
                <Typography sx={{ flexGrow: 1 }}>{t('sch:quick.walk_in')}</Typography>
                <Typography variant="caption" component="kbd" dir="ltr" color="text.secondary" sx={{ ml: 2 }}>
                  {t('sch:quick.walk_in_shortcut')}
                </Typography>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Stack>
  );
}
